import React, { useState, useEffect, useRef } from "react";
import { Shirt, X, Loader2, Tag, Check, AlertTriangle, ChevronDown } from "lucide-react";
import api from "../../../connector/api";

const INTERVENTION_CHOICES = [
  { value: "verbal", label: "Verbal Warning" },
  { value: "telegram", label: "Telegram Parent Notice" },
  { value: "call", label: "Parent Phone Call" },
  { value: "detention", label: "Detention / Escalation" },
];

const ITEM_PRESETS = [
  "No ID Card",
  "No Tie",
  "No PE Uniform",
  "Wrong Shoes",
  "Wrong Hair Color",
  "No School Logo",
  "Wrong Shirt Sleeves",
  "Wrong ID string",
];

export const UniformInfractionModal = ({
  isOpen,
  onClose,
  studentId,
  currentPeriodId,
  assignedClassesList = [],
  onSuccess,
  existingInfraction = null,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    intervention_applied: "verbal",
    subject: "",
    detailed_note: "",
  });

  // Custom Dropdown Open States
  const [isInterventionOpen, setIsInterventionOpen] = useState(false);
  const [isSubjectOpen, setIsSubjectOpen] = useState(false);

  // Refs for detecting clicks outside the dropdown popups
  const interventionRef = useRef(null);
  const subjectRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (interventionRef.current && !interventionRef.current.contains(event.target)) {
        setIsInterventionOpen(false);
      }
      if (subjectRef.current && !subjectRef.current.contains(event.target)) {
        setIsSubjectOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsInterventionOpen(false);
      setIsSubjectOpen(false);

      if (existingInfraction) {
        let parsedItems = [];
        if (Array.isArray(existingInfraction.specific_item)) {
          parsedItems = existingInfraction.specific_item;
        } else if (typeof existingInfraction.specific_item === "string") {
          parsedItems = existingInfraction.specific_item
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);
        }

        setSelectedItems(parsedItems);
        setFormData({
          intervention_applied: existingInfraction.intervention_applied || "verbal",
          subject: existingInfraction.subject ? String(existingInfraction.subject) : "",
          detailed_note: existingInfraction.detailed_note || "",
        });
      } else {
        setSelectedItems([]);
        setFormData({
          intervention_applied: "verbal",
          subject: "",
          detailed_note: "",
        });
      }
    }
  }, [isOpen, existingInfraction]);

  if (!isOpen) return null;

  const toggleSpecificItem = (item) => {
    setSelectedItems((prev) =>
      prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedItems.length === 0) {
      setError("Please select at least one violation item.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload = {
      student: studentId,
      specific_item: selectedItems.join(", "),
      intervention_applied: formData.intervention_applied,
      subject: formData.subject || null,
      detailed_note: formData.detailed_note,
      school_year_term: currentPeriodId || null,
    };

    try {
      if (existingInfraction?.id) {
        await api.patch(`teacher/uniform-infractions/${existingInfraction.id}/`, payload);
      } else {
        await api.post("teacher/uniform-infractions/", payload);
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to save uniform infraction:", err);
      const backendError = err.response?.data?.detail || "Failed to log uniform infraction. Please try again.";
      setError(backendError);
    } finally {
      setSubmitting(false);
    }
  };

  const isEditing = Boolean(existingInfraction);

  const selectedInterventionLabel =
    INTERVENTION_CHOICES.find((item) => item.value === formData.intervention_applied)?.label ||
    "Select Intervention";

  const selectedSubjectObject = assignedClassesList.find(
    (item) => String(item.id) === String(formData.subject)
  );
  const selectedSubjectLabel = selectedSubjectObject
    ? selectedSubjectObject.display_name || selectedSubjectObject.name || selectedSubjectObject.subject_name
    : "Select Class / Subject";

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white border border-zinc-200 rounded-xl shadow-xl w-full max-w-3xl overflow-visible animate-in fade-in zoom-in-95 duration-150">
        
        {/* Modal Header */}
        <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50 rounded-t-xl">
          <div className="flex items-center gap-2 text-zinc-800 font-bold text-sm">
            <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
              <Shirt className="w-4 h-4 text-amber-600" />
            </div>
            {isEditing ? "Edit Uniform Infraction" : "Log Uniform Infraction"}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 p-1 rounded-md transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4 text-xs">
          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Multi-Select Violation Items */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-zinc-600 font-medium flex items-center gap-1.5">
                <Tag className="w-3.5 h-3.5 text-zinc-400" />
                Specific Violation Items *
              </label>
              <span className="text-[10px] text-zinc-400">
                {selectedItems.length} selected
              </span>
            </div>

            <div className="p-2 bg-zinc-50 border border-zinc-200 rounded-lg flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
              {ITEM_PRESETS.map((item) => {
                const isSelected = selectedItems.includes(item);
                return (
                  <button
                    key={item}
                    type="button"
                    disabled={submitting}
                    onClick={() => toggleSpecificItem(item)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all flex items-center gap-1 cursor-pointer disabled:opacity-50 ${
                      isSelected
                        ? "bg-amber-600 text-white shadow-xs font-semibold"
                        : "bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-100"
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                    <span>{item}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Dropdown: Intervention Applied */}
          <div className="relative" ref={interventionRef}>
            <label className="block text-zinc-600 font-medium mb-1">
              Intervention Applied
            </label>
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                setIsInterventionOpen((prev) => !prev);
                setIsSubjectOpen(false);
              }}
              className="w-full px-3 py-2 border border-zinc-200 rounded-lg bg-white flex items-center justify-between text-zinc-800 hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors cursor-pointer disabled:opacity-50"
            >
              <span className="truncate">{selectedInterventionLabel}</span>
              <ChevronDown
                className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
                  isInterventionOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isInterventionOpen && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
                {INTERVENTION_CHOICES.map((choice) => {
                  const isSelected = formData.intervention_applied === choice.value;
                  return (
                    <button
                      key={choice.value}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, intervention_applied: choice.value });
                        setIsInterventionOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left flex items-center justify-between transition-colors cursor-pointer ${
                        isSelected
                          ? "bg-amber-50 text-amber-700 font-medium"
                          : "hover:bg-zinc-50 text-zinc-700"
                      }`}
                    >
                      <span>{choice.label}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-amber-600" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Custom Dropdown: Subject Class */}
          <div className="relative" ref={subjectRef}>
            <label className="block text-zinc-600 font-medium mb-1">
              Subject Class (Optional)
            </label>
            <button
              type="button"
              disabled={submitting}
              onClick={() => {
                setIsSubjectOpen((prev) => !prev);
                setIsInterventionOpen(false);
              }}
              className="w-full px-3 py-2 border border-zinc-200 rounded-lg bg-white flex items-center justify-between text-zinc-800 hover:border-zinc-300 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-colors cursor-pointer disabled:opacity-50"
            >
              <span className={`truncate ${!formData.subject ? "text-zinc-400" : ""}`}>
                {selectedSubjectLabel}
              </span>
              <ChevronDown
                className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
                  isSubjectOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isSubjectOpen && (
              <div className="absolute z-30 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, subject: "" });
                    setIsSubjectOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left flex items-center justify-between transition-colors cursor-pointer ${
                    !formData.subject
                      ? "bg-amber-50 text-amber-700 font-medium"
                      : "hover:bg-zinc-50 text-zinc-400"
                  }`}
                >
                  <span>Select Class / Subject</span>
                  {!formData.subject && <Check className="w-3.5 h-3.5 text-amber-600" />}
                </button>
                {assignedClassesList.map((item) => {
                  const itemLabel = item.display_name || item.name || item.subject_name;
                  const isSelected = String(formData.subject) === String(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, subject: String(item.id) });
                        setIsSubjectOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left flex items-center justify-between transition-colors cursor-pointer ${
                        isSelected
                          ? "bg-amber-50 text-amber-700 font-medium"
                          : "hover:bg-zinc-50 text-zinc-700"
                      }`}
                    >
                      <span>{itemLabel}</span>
                      {isSelected && <Check className="w-3.5 h-3.5 text-amber-600" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Detailed Note */}
          <div>
            <label className="block text-zinc-600 font-medium mb-1">
              Detailed Note
            </label>
            <textarea
              rows={3}
              placeholder="Additional context or comments..."
              value={formData.detailed_note}
              onChange={(e) =>
                setFormData({ ...formData, detailed_note: e.target.value })
              }
              className="w-full px-3 py-2 border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none custom-scroll"
            />
          </div>

          {/* Footer Actions */}
          <div className="pt-2 flex items-center justify-end gap-2 border-t border-zinc-100">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-zinc-600 hover:bg-zinc-100 rounded-lg font-medium transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || selectedItems.length === 0}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors inline-flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
            >
              {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isEditing ? "Update Infraction" : "Save Infraction"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};